import { ApiUserEnvironment } from 'api/userEnvironment/types/ApiUserEnvironment';
import { del } from 'utils/http/del';
import { getJson } from 'utils/http/getJson';
import { patchJson } from 'utils/http/patchJson';
import { postJson } from 'utils/http/postJson';
import { putJson } from 'utils/http/putJson';

import { ApiApplicantProfile } from './types/ApiApplicantProfile';
import { ApiWorkHistoryJob } from './types/ApiWorkHistoryJob';

export type ApiApplicantProfileResponse = {
  resumeId: string | null;
  isRecruitable: boolean | null;
  applicantProfile: ApiApplicantProfile | null;
  userEnvironment?: ApiUserEnvironment; // Only present when using apiUpdateApplicantProfile
};

// NOTE: This needs to be kept consistent with any default states in the model.
const emptyProfile = {
  resumeId: null,
  isRecruitable: null,
  workHistoryJobs: [],
  skills: [],
  willingToWorkOnsite: false,
  willingToWorkRemote: false,
  willingToWorkHybrid: false,
  willingToWorkPartTime: false,
  userLocation: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapProfileResponse(response: any) {
  const {
    resumeS3FileId: resumeId,
    isRecruitable,
    ...profile
  } = response.applicantProfile || emptyProfile;
  return {
    ...response,
    resumeId,
    isRecruitable,
    applicantProfile: profile,
  };
}

export async function apiFetchApplicantProfile(): Promise<ApiApplicantProfileResponse> {
  return getJson('/data/userdashboard/applicant-profile/profile').then(
    mapProfileResponse,
  );
}

export async function apiUpdateApplicantProfile(
  values: Record<string, unknown>,
) {
  return patchJson<
    ApiApplicantProfileResponse & { userEnvironment: ApiUserEnvironment }
  >('/data/userdashboard/applicant-profile/profile', values).then(
    mapProfileResponse,
  );
}

export async function apiAddWorkHistoryJob(
  job: ApiWorkHistoryJob,
): Promise<ApiApplicantProfileResponse> {
  return postJson(
    '/data/userdashboard/applicant-profile/work-history/jobs',
    job,
  ).then(mapProfileResponse);
}

export async function apiEditWorkHistoryJob(
  id: string,
  job: ApiWorkHistoryJob,
): Promise<ApiApplicantProfileResponse> {
  return putJson(
    `/data/userdashboard/applicant-profile/work-history/jobs/${id}`,
    job,
  ).then(mapProfileResponse);
}

export async function apiDeleteWorkHistoryJob(
  id: string,
): Promise<ApiApplicantProfileResponse> {
  return del(
    `/data/userdashboard/applicant-profile/work-history/jobs/${id}`,
  ).then(mapProfileResponse);
}

export async function apiFetchUserByEmail(email: string) {
  return getJson<{ userExists: boolean } | null>(
    `/data/users/by-email?email=${encodeURIComponent(email)}`,
  ).then((result) => result && result.userExists);
}
